'use client'

import { AutoAwesome, Build, CheckCircle, ExpandMore } from '@mui/icons-material'
import { Box, ButtonBase, Collapse, Stack, Typography, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Shared chat transcript styling for assistant surfaces (generative dashboard page, shared-conversation
 * page), mirroring the AssistantBar so all assistant chat UIs feel like one product.
 */

export const UserBubble = styled(Box)(({ theme }) => ({
  maxWidth: '85%',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(2),
  borderBottomRightRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: theme.typography.body2.fontSize,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
}))

export const AssistantBubble = styled(Box)(({ theme }) => ({
  maxWidth: '85%',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(2),
  borderBottomLeftRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  fontSize: theme.typography.body2.fontSize,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
}))

export const StatusChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  alignSelf: 'flex-start',
  maxWidth: '100%',
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 999,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary
}))

/** Rounded card for multi-line action summaries — avoids StatusChip's pill radius. */
export const ActionSummaryCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  wordBreak: 'break-word'
}))

type AssistantMessageStackProps = {
  activity: ReactNode
  /**
   * When true, render Activity above the answer (chronological / completed turns).
   * When false, keep Activity under the bubble so the live Working row stays at the
   * bottom while streamed text grows and the transcript auto-scrolls.
   */
  activityFirst?: boolean
  content?: ReactNode
}

/**
 * Stacks the activity row and assistant bubble for one turn.
 *
 * Live turns keep Activity under the bubble so Working stays visible while text
 * streams. Completed turns put Activity first so the answer sits at the bottom
 * of the viewport (no scroll-up past tool details to read the response).
 */
export function AssistantMessageStack({
  activity,
  activityFirst = false,
  content
}: AssistantMessageStackProps) {
  return (
    <Box
      data-testid="assistant-message-stack"
      // minWidth: 0 lets a wide table scroll inside the bubble instead of stretching the column.
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: '92%', minWidth: 0 }}
    >
      {activityFirst ? (
        <>
          {activity}
          {content}
        </>
      ) : (
        <>
          {content}
          {activity}
        </>
      )}
    </Box>
  )
}

export type AssistantActivityItem = {
  id: string
  label: string
  detail?: ReactNode
  kind: 'tool' | 'skill' | 'reasoning'
  status?: 'running' | 'done'
}

export type AssistantActivityLabels = {
  activity: string
  collapse: string
  completed: string
  done: string
  emptyDetails: string
  expand: string
  reasoning: string
  running: string
  skillCount: (count: number) => string
  thinking: string
  toolCount: (count: number) => string
  working: string
}

type AssistantActivityProps = {
  active?: boolean
  items?: AssistantActivityItem[]
  labels: AssistantActivityLabels
  phaseMessage?: string
  reasoning?: string
}

export function AssistantActivity({
  active = false,
  items = [],
  labels,
  phaseMessage,
  reasoning
}: AssistantActivityProps) {
  const [open, setOpen] = useState(false)
  const normalizedReasoning = reasoning?.trim() ?? ''
  const detailItems = useMemo(() => {
    if (!normalizedReasoning) {
      return items
    }
    return [
      ...items,
      {
        id: 'reasoning',
        label: labels.reasoning,
        detail: normalizedReasoning,
        kind: 'reasoning' as const,
        status: 'done' as const
      }
    ]
  }, [items, labels.reasoning, normalizedReasoning])
  const running = active || detailItems.some((item) => item.status === 'running')
  const hasActivity = running || detailItems.length > 0 || Boolean(phaseMessage)

  if (!hasActivity) {
    return null
  }

  const toolCount = detailItems.filter((item) => item.kind === 'tool').length
  const skillCount = detailItems.filter((item) => item.kind === 'skill').length
  const summary = [
    toolCount > 0 ? labels.toolCount(toolCount) : null,
    skillCount > 0 ? labels.skillCount(skillCount) : null
  ]
    .filter(Boolean)
    .join(' · ')
  const title =
    phaseMessage || (running ? (items.length > 0 ? labels.working : labels.thinking) : labels.completed)

  return (
    <ActivityRoot>
      <ActivityToggle
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-label={open ? labels.collapse : labels.expand}
      >
        {running ? (
          <ThinkingDots aria-hidden="true">
            <span />
          </ThinkingDots>
        ) : (
          <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
        )}
        <ActivityText>
          <Typography variant="body2" color="text.secondary" fontWeight={600} noWrap>
            {title}
          </Typography>
          {summary ? (
            <Typography variant="caption" color="text.disabled" noWrap>
              {summary}
            </Typography>
          ) : null}
        </ActivityText>
        <ExpandMore
          sx={{
            fontSize: 18,
            color: 'text.disabled',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 160ms ease'
          }}
        />
      </ActivityToggle>
      <Collapse in={open} unmountOnExit>
        <ActivityDetails>
          {detailItems.length > 0 ? (
            detailItems.map((item) => (
              <ActivityDetail key={item.id}>
                <ActivityDetailIcon item={item} labels={labels} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {item.label}
                  </Typography>
                  {item.detail ? (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                    >
                      {item.detail}
                    </Typography>
                  ) : null}
                </Box>
              </ActivityDetail>
            ))
          ) : (
            <Typography variant="caption" color="text.disabled">
              {labels.emptyDetails}
            </Typography>
          )}
        </ActivityDetails>
      </Collapse>
    </ActivityRoot>
  )
}

function ActivityDetailIcon({ item, labels }: { item: AssistantActivityItem; labels: AssistantActivityLabels }) {
  if (item.kind === 'reasoning') {
    return <AutoAwesome titleAccess={labels.reasoning} sx={{ fontSize: 14, color: 'text.disabled' }} />
  }

  if (item.status === 'running') {
    return (
      <ThinkingDots aria-label={labels.running} $small>
        <span />
      </ThinkingDots>
    )
  }

  if (item.status === 'done') {
    return <CheckCircle titleAccess={labels.done} sx={{ fontSize: 14, color: 'success.main' }} />
  }

  return <Build sx={{ fontSize: 14, color: 'text.disabled' }} />
}

const ActivityRoot = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-start',
  maxWidth: '100%',
  color: theme.palette.text.secondary
}))

const ActivityToggle = styled(ButtonBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  maxWidth: '100%',
  minHeight: 30,
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.spacing(1),
  textAlign: 'left',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}))

const ActivityText = styled(Box)({
  minWidth: 0,
  display: 'flex',
  alignItems: 'baseline',
  gap: 6
})

const ActivityDetails = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(1.75),
  padding: theme.spacing(0.75, 1),
  borderLeft: `1px solid ${theme.palette.divider}`
}))

const ActivityDetail = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '16px minmax(0, 1fr)',
  alignItems: 'start',
  gap: theme.spacing(0.75)
}))

const ThinkingDots = styled('span', {
  shouldForwardProp: (prop) => prop !== '$small'
})<{ $small?: boolean }>(({ theme, $small }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: $small ? 14 : 18,
  height: $small ? 10 : 12,
  flexShrink: 0,
  '&::before, &::after, & span': {
    content: '""',
    width: $small ? 3 : 4,
    height: $small ? 3 : 4,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.text.secondary, 0.75),
    animation: 'assistant-activity-pulse 1.05s ease-in-out infinite'
  },
  '& span': {
    animationDelay: '150ms'
  },
  '&::after': {
    animationDelay: '300ms'
  },
  '@keyframes assistant-activity-pulse': {
    '0%, 80%, 100%': {
      opacity: 0.35,
      transform: 'translateY(0)'
    },
    '40%': {
      opacity: 1,
      transform: 'translateY(-2px)'
    }
  }
}))
